import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import FileDocumentPreview from '../FileDocumentPreview';
import { DocumentoParaAssinatura } from '../../utils/documentosAssinatura';
import { useToastValue } from '../../contexts/toastMessage';
import { loginRedirectPrivider } from '../../services/auth';
import {
  isAssinaturaElegibilidadeFailure,
  validarElegibilidadeAssinaturaGovbr,
} from '../../services/assinaturaElegibilidade';
import {
  abrirAutorizacaoGovbr,
  aguardarAutorizacaoGovbr,
  assinarDocumentoGovbr,
  confirmarAssinaturaDocumento,
  formatAssinaturaGovbrError,
  prepararAssinaturaDocumento,
  PrepararAssinaturaResponse,
} from '../../services/assinatura-govbr';

type Props = {
  open: boolean;
  documentos: DocumentoParaAssinatura[];
  signedDocumentIds: Set<string>;
  initialIndex?: number;
  onClose: () => void;
  onDocumentSigned: (documentoId: string) => void;
  onSignComplete?: () => void;
  onNivelInsuficiente?: (titulo: string) => void;
};

const useStyles = makeStyles(() => ({
  dialogPaper: {
    minWidth: 'min(960px, 95vw)',
  },
  navBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
    gap: 8,
  },
  navInfo: {
    flex: 1,
    textAlign: 'center',
    fontFamily: 'Roboto',
    fontSize: 14,
    color: '#373F41',
  },
  navButton: {
    color: '#0F6FB7',
  },
  navButtonDisabled: {
    color: '#B0BEC5',
  },
  signButton: {
    backgroundColor: '#0F6FB7',
    color: '#fff',
    textTransform: 'uppercase',
    fontFamily: 'Roboto',
    fontWeight: 500,
    '&:hover': {
      backgroundColor: '#0c5a94',
    },
  },
  signedButton: {
    backgroundColor: '#E8F5E9',
    color: '#43A047',
    textTransform: 'uppercase',
    fontFamily: 'Roboto',
    fontWeight: 500,
  },
  confirmOk: {
    backgroundColor: '#0F6FB7',
    color: '#fff',
  },
  dialogTitle: {
    color: '#373F41',
  },
  dialogText: {
    color: '#373F41',
    fontFamily: 'Roboto',
    fontSize: 16,
    fontWeight: 500,
  },
  confirmDialog: {
    zIndex: 1400,
  },
  errorText: {
    color: '#D32F2F',
    marginTop: 8,
    fontSize: 14,
  },
  infoText: {
    color: '#373F41',
    marginTop: 8,
    marginBottom: 8,
    fontSize: 14,
    lineHeight: 1.5,
  },
}));

const executarAssinaturaGovbr = async (
  documentoId: string,
  preparar: PrepararAssinaturaResponse,
): Promise<string> => {
  const popup = abrirAutorizacaoGovbr(preparar.authorization_url);
  if (!popup) {
    throw new Error('Não foi possível abrir a janela de autorização Gov.br.');
  }

  const autorizacao = await aguardarAutorizacaoGovbr();
  if (!autorizacao.code) {
    throw new Error('Código de autorização Gov.br não recebido.');
  }

  const { data } = await assinarDocumentoGovbr(documentoId, autorizacao.code);
  if (!data.documento_assinado_base64) {
    const traceInfo = data.trace_id ? ` (trace_id: ${data.trace_id})` : '';
    throw new Error(
      (data.detail || data.message || 'O assinador não retornou o PDF assinado.') + traceInfo,
    );
  }

  return data.documento_assinado_base64;
};

export default function AssinarDocumentosDialog({
  open,
  documentos,
  signedDocumentIds,
  initialIndex = 0,
  onClose,
  onDocumentSigned,
  onSignComplete,
  onNivelInsuficiente,
}: Props): JSX.Element {
  const classes = useStyles();
  const location = useLocation();
  const { setToast } = useToastValue();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [signing, setSigning] = useState(false);
  const [validating, setValidating] = useState(false);
  const [signError, setSignError] = useState<string | null>(null);
  const [sessionMessage, setSessionMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setConfirmOpen(false);
      setSigning(false);
      setValidating(false);
      setSignError(null);
      setSessionMessage(null);
      return;
    }

    const safeIndex = Math.min(
      Math.max(initialIndex, 0),
      Math.max(documentos.length - 1, 0),
    );
    setCurrentIndex(safeIndex);
    setSignError(null);
    setSessionMessage(null);
  }, [open, initialIndex, documentos.length]);

  const currentDocument = documentos[currentIndex];
  const total = documentos.length;
  const hasMultiple = total > 1;
  const isCurrentSigned = currentDocument
    ? signedDocumentIds.has(currentDocument.id)
    : false;

  const handlePrevious = useCallback(() => {
    setCurrentIndex(index => Math.max(index - 1, 0));
  }, []);

  const handleNext = useCallback(() => {
    setCurrentIndex(index => Math.min(index + 1, total - 1));
  }, [total]);

  const handleConfirmSign = useCallback(async () => {
    if (!currentDocument || signing) {
      return;
    }

    setSigning(true);
    setSignError(null);

    try {
      const { data: preparar } = await prepararAssinaturaDocumento(currentDocument.id);
      const documentoAssinadoBase64 = await executarAssinaturaGovbr(
        currentDocument.id,
        preparar,
      );

      await confirmarAssinaturaDocumento(
        currentDocument.id,
        documentoAssinadoBase64,
      );

      onDocumentSigned(currentDocument.id);
      setToast({
        visible: true,
        message: 'Documento assinado com sucesso',
        type: 'success',
      });
      onSignComplete?.();
      setConfirmOpen(false);
    } catch (error: any) {
      setSignError(formatAssinaturaGovbrError(error));
    } finally {
      setSigning(false);
    }
  }, [
    currentDocument,
    onDocumentSigned,
    onSignComplete,
    setToast,
    signing,
  ]);

  const handleOpenConfirm = useCallback(async () => {
    if (signing || validating) {
      return;
    }

    setSignError(null);
    setSessionMessage(null);
    setValidating(true);

    try {
      const result = await validarElegibilidadeAssinaturaGovbr();

      if (isAssinaturaElegibilidadeFailure(result)) {
        if (result.reason === 'nivel') {
          onNivelInsuficiente?.(result.titulo);
          return;
        }
        if (result.reason === 'session') {
          setSessionMessage(result.message);
          return;
        }
        setSignError(result.message);
        return;
      }

      setConfirmOpen(true);
    } finally {
      setValidating(false);
    }
  }, [onNivelInsuficiente, signing, validating]);

  const handleLoginRedirect = useCallback(() => {
    loginRedirectPrivider(`${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);

  const handleCloseConfirm = useCallback(() => {
    if (signing) {
      return;
    }
    setConfirmOpen(false);
    setSignError(null);
  }, [signing]);

  const footerAction = useMemo(() => {
    if (!currentDocument) {
      return null;
    }

    if (isCurrentSigned) {
      return (
        <Button
          className={classes.signedButton}
          variant='contained'
          disabled
          fullWidth
        >
          Assinado
        </Button>
      );
    }

    if (signing || validating) {
      return (
        <Button className={classes.signButton} variant='contained' disabled fullWidth>
          <CircularProgress size={20} color='inherit' />
        </Button>
      );
    }

    if (sessionMessage) {
      return (
        <Button
          className={classes.signButton}
          variant='contained'
          fullWidth
          onClick={handleLoginRedirect}
        >
          Fazer login
        </Button>
      );
    }

    return (
      <Button
        className={classes.signButton}
        variant='contained'
        fullWidth
        onClick={handleOpenConfirm}
      >
        Assinar
      </Button>
    );
  }, [
    classes.signButton,
    classes.signedButton,
    currentDocument,
    handleLoginRedirect,
    handleOpenConfirm,
    isCurrentSigned,
    sessionMessage,
    signing,
    validating,
  ]);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        classes={{ paper: classes.dialogPaper }}
      >
        <DialogTitle className={classes.dialogTitle}>
          {currentDocument?.titulo || currentDocument?.assunto || 'Documento'}
        </DialogTitle>
        <DialogContent dividers>
          {hasMultiple && (
            <Box className={classes.navBar}>
              <IconButton
                aria-label='documento anterior'
                onClick={handlePrevious}
                disabled={currentIndex === 0 || signing}
                className={
                  currentIndex === 0
                    ? classes.navButtonDisabled
                    : classes.navButton
                }
              >
                <MdChevronLeft size={28} />
              </IconButton>
              <Typography className={classes.navInfo}>
                Documento {currentIndex + 1} de {total}
              </Typography>
              <IconButton
                aria-label='próximo documento'
                onClick={handleNext}
                disabled={currentIndex >= total - 1 || signing}
                className={
                  currentIndex >= total - 1
                    ? classes.navButtonDisabled
                    : classes.navButton
                }
              >
                <MdChevronRight size={28} />
              </IconButton>
            </Box>
          )}
          {sessionMessage && (
            <Typography className={classes.infoText}>{sessionMessage}</Typography>
          )}
          {signError && (
            <Typography className={classes.errorText}>{signError}</Typography>
          )}
          {currentDocument ? (
            <FileDocumentPreview
              previewUrl={currentDocument.preview_url}
              title={currentDocument.titulo || currentDocument.assunto}
              height='55vh'
              showDownload={false}
              footerAction={footerAction}
            />
          ) : (
            <Typography className={classes.dialogText}>
              Nenhum documento disponível para assinatura.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color='default' disabled={signing}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmOpen}
        onClose={handleCloseConfirm}
        className={classes.confirmDialog}
        disableEnforceFocus
        aria-labelledby='confirmar-assinatura-titulo'
        aria-describedby='confirmar-assinatura-texto'
      >
        <DialogTitle
          id='confirmar-assinatura-titulo'
          className={classes.dialogTitle}
        >
          Confirmar assinatura
        </DialogTitle>
        <DialogContent>
          <Typography
            id='confirmar-assinatura-texto'
            className={classes.dialogText}
            component='div'
          >
            <Box component='p' m={0} mb={1.5}>
              Este serviço está disponível apenas para perfis Prata e/ou Ouro no
              Gov.br.
            </Box>
            <Box component='p' m={0} mb={1.5}>
              Você será redirecionado ao Gov.br para autorizar a assinatura digital
              deste documento.
            </Box>
            <Box component='p' m={0} fontWeight={600}>
              Tem certeza?
            </Box>
          </Typography>
          {signError && (
            <Typography className={classes.errorText}>{signError}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseConfirm}
            color='default'
            disabled={signing}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmSign}
            variant='contained'
            className={classes.confirmOk}
            disabled={signing}
          >
            {signing ? <CircularProgress size={20} color='inherit' /> : 'OK'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
